import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  Tag,
  Progress,
  Typography,
  Space,
  Popconfirm,
  message,
  Statistic,
  Row,
  Col,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EyeOutlined,
  ReloadOutlined,
  ThunderboltOutlined,
  FolderAddOutlined,
} from '@ant-design/icons';
import axios from 'axios';

const { Title, Text } = Typography;

interface ModerationSession {
  id: number;
  name: string;
  dataset_id: number;
  dataset_name: string;
  status: string;
  total_images: number;
  current_index: number;
  approved_count: number;
  declined_count: number;
  skipped_count: number;
  elapsed_seconds: number;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  source_type: string;
  source_session_id: number | null;
  source_session_name: string | null;
}

interface AutoModSession {
  id: number;
  name: string;
  status: string;
  total_images: number;
  classified_count: number;
  routed_count: number;
  excluded_count: number;
  failed_count: number;
  created_at: string;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

const formatDuration = (seconds: number): string => {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${mins}m`;
};

const ModerationSessionList: React.FC = () => {
  const [sessions, setSessions] = useState<ModerationSession[]>([]);
  const [autoSessions, setAutoSessions] = useState<AutoModSession[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const refreshAll = () => { fetchSessions(); fetchAuto(); };

  useEffect(() => {
    refreshAll();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (sessions.some(s => s.status === 'active') || autoSessions.some(s => s.status === 'running')) {
      intervalRef.current = setInterval(refreshAll, 5000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [sessions, autoSessions]);

  const fetchSessions = async () => {
    try {
      const response = await axios.get(`${API_URL}/moderation-sessions`);
      setSessions(response.data);
    } catch (error) {
      console.error('Failed to fetch moderation sessions:', error);
      message.error('Failed to load moderation sessions');
    } finally {
      setLoading(false);
    }
  };

  const fetchAuto = async () => {
    try {
      const response = await axios.get(`${API_URL}/auto-moderation-sessions`);
      setAutoSessions(response.data);
    } catch (error) {
      console.error('Failed to fetch auto-moderation sessions:', error);
    }
  };

  const handleDeleteAuto = async (id: number) => {
    try {
      await axios.delete(`${API_URL}/auto-moderation-sessions/${id}`);
      message.success('Auto-moderation session deleted');
      fetchAuto();
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to delete session');
    }
  };

  const getAutoStatusTag = (status: string) => {
    const config: Record<string, string> = {
      pending: 'default', running: 'processing', paused: 'warning',
      completed: 'success', failed: 'error',
    };
    return <Tag color={config[status] || 'default'}>{status.toUpperCase()}</Tag>;
  };

  const autoColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, r: AutoModSession) => (
        <a onClick={() => navigate(`/auto-moderation/${r.id}`)}>{name}</a>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (s: string) => getAutoStatusTag(s),
    },
    {
      title: 'Progress',
      key: 'progress',
      render: (_: any, r: AutoModSession) => {
        const total = r.total_images || 1;
        const percent = Math.round(((r.classified_count + r.failed_count) / total) * 100);
        return (
          <div style={{ minWidth: 170 }}>
            <Progress percent={percent} size="small"
              status={r.status === 'completed' ? 'success' : r.status === 'failed' ? 'exception' : 'active'} />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {r.classified_count.toLocaleString()} / {total.toLocaleString()}
            </Text>
          </div>
        );
      },
    },
    {
      title: 'Routed',
      dataIndex: 'routed_count',
      key: 'routed_count',
      render: (n: number) => <Text style={{ color: '#1890ff' }}><FolderAddOutlined /> {n.toLocaleString()}</Text>,
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (d: string) => new Date(d).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, r: AutoModSession) => (
        <Space>
          <Button icon={<EyeOutlined />} size="small" onClick={() => navigate(`/auto-moderation/${r.id}`)}>
            View
          </Button>
          <Popconfirm title="Delete this session?" onConfirm={() => handleDeleteAuto(r.id)}
            okText="Delete" cancelText="Cancel">
            <Button danger icon={<DeleteOutlined />} size="small" disabled={r.status === 'running'} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleDelete = async (id: number) => {
    try {
      await axios.delete(`${API_URL}/moderation-sessions/${id}`);
      message.success('Session deleted');
      fetchSessions();
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to delete session');
    }
  };

  const getStatusTag = (status: string) => {
    const config: Record<string, { color: string }> = {
      pending: { color: 'default' },
      active: { color: 'processing' },
      paused: { color: 'warning' },
      completed: { color: 'success' },
    };
    const c = config[status] || { color: 'default' };
    return <Tag color={c.color}>{status.toUpperCase()}</Tag>;
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: ModerationSession) => (
        <a onClick={() => navigate(`/moderation/${record.id}`)}>{name}</a>
      ),
    },
    {
      title: 'Source',
      key: 'source',
      render: (_: any, record: ModerationSession) => (
        record.source_type === 'session' ? (
          <Tag color="purple">
            ↳ {record.source_session_id
              ? <a onClick={(e) => { e.stopPropagation(); navigate(`/moderation/${record.source_session_id}`); }}>
                  {record.source_session_name || `#${record.source_session_id}`}
                </a>
              : 'source removed'}
          </Tag>
        ) : (
          <Text>{record.dataset_name}</Text>
        )
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => getStatusTag(status),
    },
    {
      title: 'Progress',
      key: 'progress',
      render: (_: any, record: ModerationSession) => {
        const total = record.total_images || 1;
        const reviewed = record.approved_count + record.declined_count + record.skipped_count;
        const percent = Math.round((reviewed / total) * 100);
        return (
          <div style={{ minWidth: 150 }}>
            <Progress percent={percent} size="small" status={record.status === 'completed' ? 'success' : 'active'} />
            <Text type="secondary" style={{ fontSize: 12 }}>
              <Text style={{ color: '#52c41a', fontSize: 12 }}>{record.approved_count}</Text>
              {' / '}
              <Text style={{ color: '#ff4d4f', fontSize: 12 }}>{record.declined_count}</Text>
              {' / '}
              {total.toLocaleString()} total
            </Text>
          </div>
        );
      },
    },
    {
      title: 'Time Spent',
      key: 'time',
      render: (_: any, record: ModerationSession) => (
        <Text>{formatDuration(record.elapsed_seconds || 0)}</Text>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: ModerationSession) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            onClick={() => navigate(`/moderation/${record.id}`)}
            size="small"
          >
            View
          </Button>
          <Popconfirm
            title="Delete this session?"
            onConfirm={() => handleDelete(record.id)}
            okText="Delete"
            cancelText="Cancel"
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              size="small"
              disabled={record.status === 'active'}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const totals = sessions.reduce(
    (acc, s) => ({
      total: acc.total + s.total_images,
      approved: acc.approved + s.approved_count,
      declined: acc.declined + s.declined_count,
      active: acc.active + (s.status === 'active' ? 1 : 0),
    }),
    { total: 0, approved: 0, declined: 0, active: 0 }
  );

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <EyeOutlined style={{ fontSize: 28, color: '#6366f1' }} />
            <Title level={2} style={{ margin: 0 }}>Moderation Sessions</Title>
          </div>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={refreshAll}>Refresh</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/moderation/create')}>
              New Session
            </Button>
          </Space>
        </div>

        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}><Statistic title="Total Sessions" value={sessions.length} /></Col>
          <Col span={6}><Statistic title="Active" value={totals.active} valueStyle={{ color: '#1890ff' }} /></Col>
          <Col span={6}><Statistic title="Total Approved" value={totals.approved} valueStyle={{ color: '#52c41a' }} /></Col>
          <Col span={6}><Statistic title="Total Declined" value={totals.declined} valueStyle={{ color: '#ff4d4f' }} /></Col>
        </Row>

        <Table
          columns={columns}
          dataSource={sessions}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      {autoSessions.length > 0 && (
        <Card style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <ThunderboltOutlined style={{ fontSize: 22, color: '#6366f1' }} />
              <Title level={4} style={{ margin: 0 }}>Auto-Moderation Sessions</Title>
            </div>
            <Button type="link" onClick={() => navigate('/auto-moderation')}>Open Auto-Moderation →</Button>
          </div>
          <Table
            columns={autoColumns}
            dataSource={autoSessions}
            rowKey="id"
            pagination={{ pageSize: 10 }}
          />
        </Card>
      )}
    </div>
  );
};

export default ModerationSessionList;
